import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ScrollText, BookOpen, CalendarHeart, Flame, GraduationCap, Sparkles, Landmark, ChevronRight, ShieldCheck } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { SiteHeader, SiteFooter } from "@/components/SiteHeader";

const API = process.env.REACT_APP_BACKEND_URL;

const MODULES = [
  { to: "/stotras", icon: ScrollText, kn: "ಸ್ತೋತ್ರ ಗ್ರಂಥಾಲಯ", en: "Stotra Library", key: "stotras",
    desc: "Bilingual stotras with meaning & transliteration, preserved digitally." },
  { to: "/rituals", icon: Flame, kn: "ವಿಧಿ ವಿಧಾನ", en: "Ritual Vidhi", key: "rituals_documented",
    desc: "Step-by-step samskara & pooja procedures documented for every family." },
  { to: "/festivals", icon: CalendarHeart, kn: "ಹಬ್ಬಗಳ ಪಂಚಾಂಗ", en: "Festival Calendar", key: "festivals_documented",
    desc: "2026 Hindu festivals with vidhi, dates and preparation checklists." },
  { to: "/panchanga", icon: Sparkles, kn: "ಪಂಚಾಂಗ ಯಂತ್ರ", en: "Panchanga Engine", key: null,
    desc: "Deep-tech astronomical computation of tithi, nakshatra & muhurta." },
  { to: "/parayana", icon: BookOpen, kn: "ಪಾರಾಯಣ", en: "Parayana Guides", key: null,
    desc: "Guided recitation of sacred texts for home practice." },
  { to: "/traditions", icon: Landmark, kn: "ಸಂಪ್ರದಾಯಗಳು", en: "Traditions", key: null,
    desc: "Smartha, Madhwa, Srivaishnava and community customs, explained." },
  { to: "/patashala", icon: GraduationCap, kn: "ವೇದ ಪಾಠಶಾಲೆ", en: "Veda Patashala", key: null,
    desc: "Supporting gurukula students and the living guru–shishya parampara." },
  { to: "/patashala/sringeri", icon: Landmark, kn: "ಶೃಂಗೇರಿ ಪರಂಪರೆ", en: "Sringeri Heritage", key: null,
    desc: "The Advaita lineage of Sri Sharada Peetham that inspires our work." },
];

export default function HeritagePage() {
  const [stats, setStats] = useState(null);
  useSeo({
    title: "Vedic Heritage Archive — ಪರಂಪರೆ | Preserving Karnataka's Sacred Knowledge",
    description: "PurohithConnect's digital Vedic heritage archive — stotras, ritual vidhi, festival calendar, panchanga and the living guru–shishya parampara of Karnataka, preserved in Kannada and English for future generations.",
    canonicalPath: "/heritage",
    jsonLd: { "@context": "https://schema.org", "@type": "CollectionPage", name: "Vedic Heritage Archive", url: "https://purohithconnect.com/heritage", about: "Digital preservation of Karnataka's Vedic and ritual heritage" },
  });
  useEffect(() => {
    fetch(`${API}/api/impact/stats`)
      .then((r) => { if (!r.ok) throw new Error("bad"); return r.json(); })
      .then((d) => setStats(d.heritage)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#FBF6EC] text-[#3B221B]" style={{ colorScheme: "light" }}>
      <SiteHeader />
      <main data-testid="heritage-page">
        <section className="relative overflow-hidden border-b border-[#A8722A]/20 bg-[#F3EADA]">
          <div className="mx-auto max-w-5xl px-4 py-14 text-center md:px-8 md:py-20">
            <p className="font-baloo text-base text-[#B23A2B]">॥ ಜ್ಞಾನ ಸಂರಕ್ಷಣೆ ॥</p>
            <h1 className="mt-2 font-eczar text-4xl font-bold sm:text-5xl lg:text-6xl">Vedic Heritage Archive</h1>
            <p className="mx-auto mt-4 max-w-2xl font-mukta text-base text-[#6B5044] md:text-lg">
              ಕರ್ನಾಟಕದ ಪವಿತ್ರ ಜ್ಞಾನ, ಸ್ತೋತ್ರ, ವಿಧಿ ಮತ್ತು ಪರಂಪರೆಯನ್ನು ಮುಂದಿನ ಪೀಳಿಗೆಗೆ ಡಿಜಿಟಲ್ ರೂಪದಲ್ಲಿ ಸಂರಕ್ಷಿಸುವುದು. · A living digital archive preserving Karnataka's stotras, rituals, festivals and the guru–shishya parampara — bilingual, open and free.
            </p>
            {stats && (
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3" data-testid="heritage-stats">
                {[["Stotras", stats.stotras], ["Rituals", stats.rituals_documented], ["Festivals", stats.festivals_documented], ["Articles", stats.knowledge_articles], ["Total assets", stats.total_digitised_assets]].map(([l, v]) => (
                  <div key={l} className="rounded-[14px] border border-[#A8722A]/25 bg-[#FFFDF8] px-5 py-3">
                    <p className="font-eczar text-2xl font-bold text-[#B23A2B]">{v}</p>
                    <p className="font-mukta text-xs text-[#6B5044]">{l}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 py-14 md:px-8">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {MODULES.map((m) => {
              const Icon = m.icon;
              const count = stats && m.key ? stats[m.key] : null;
              return (
                <Link key={m.to} to={m.to} data-testid={`heritage-module-${m.en.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group flex flex-col rounded-[18px] border border-[#A8722A]/25 bg-[#FFFDF8] p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#A8722A]/12 text-[#A8722A]"><Icon size={22} /></span>
                    {count != null && <span className="font-eczar text-lg font-bold text-[#B23A2B]">{count}+</span>}
                  </div>
                  <p className="mt-4 font-baloo text-base text-[#B23A2B]">{m.kn}</p>
                  <h3 className="font-eczar text-xl font-semibold">{m.en}</h3>
                  <p className="mt-1 flex-1 font-mukta text-sm text-[#6B5044]">{m.desc}</p>
                  <span className="mt-3 inline-flex items-center gap-1 font-mukta text-sm font-semibold text-[#B23A2B]">Explore <ChevronRight size={15} className="transition-transform group-hover:translate-x-1" /></span>
                </Link>
              );
            })}
          </div>
        </section>

        <section className="border-t border-[#A8722A]/20 bg-[#F6ECDB]">
          <div className="mx-auto max-w-4xl px-4 py-14 text-center md:px-8">
            <ShieldCheck className="mx-auto text-[#A8722A]" size={34} />
            <h2 className="mt-3 font-eczar text-3xl font-semibold">Why we preserve</h2>
            <p className="mx-auto mt-3 max-w-2xl font-mukta text-base text-[#6B5044]">
              As senior purohiths age and gurukulas shrink, centuries of oral ritual knowledge risk being lost. PurohithConnect digitises this knowledge in Kannada and English, supports the purohiths who carry it, and makes it freely accessible — so the parampara lives on for the next generation.
            </p>
            <Link to="/impact" data-testid="heritage-impact-link" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#B23A2B] px-6 py-3 font-mukta font-semibold text-white hover:bg-[#9a2f22]">
              See our impact <ChevronRight size={16} />
            </Link>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
